/**
 * "Added to cart" notification.
 *
 * Replaces the cart drawer as the add-to-cart confirmation. The theme setting
 * "After adding to cart" must stay on "Do nothing" so the drawer stays shut;
 * the header cart count still updates on its own.
 *
 * This watches the network rather than the theme's own `on:cart:add` event, so
 * it also fires for adds that never go through <product-form> (quick add, the
 * Jibbitz charm buttons, app-injected buttons). Every add hits /cart/add.js, so
 * that is the one reliable signal.
 *
 * Styles: atc-toast.css
 */
(function () {
  const DURATION = 5000;

  let toast;
  let els;
  let fallbackTimer;
  // Bumped per add so a slow image load can't overwrite a newer product's.
  let imageToken = 0;

  const cartUrl = () => (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Is this request an add-to-cart? Matches locale-prefixed routes too
   * (/en-jo/cart/add.js), which is why this is a pattern and not an equality
   * check against theme.routes.cartAdd.
   * @param {string} url - Request URL.
   * @returns {boolean}
   */
  function isCartAdd(url) {
    return typeof url === 'string' && /\/cart\/add(\.js)?(\?|$)/.test(url.split('#')[0]);
  }

  /**
   * Pulls the added line item out of an /cart/add.js response body, which comes
   * back in one of two shapes: a bare line item (form posts) or {items: [...]}
   * (the AJAX API). Sections responses add keys but keep the item at the root.
   * @param {object} data - Parsed response body.
   * @returns {object|null} The line item, or null if unrecognised.
   */
  function parseItem(data) {
    if (!data || typeof data !== 'object') return null;
    if (Array.isArray(data.items) && data.items.length) return data.items[0];
    if (data.product_title || data.title) return data;
    return null;
  }

  function build() {
    if (toast) return;

    toast = document.createElement('div');
    toast.className = 'atc-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = [
      '<p class="atc-toast__status">',
      '<span class="atc-toast__check" aria-hidden="true">',
      '<svg viewBox="0 0 24 24"><path d="M4 12.5 9.5 18 20 6.5"/></svg>',
      '</span>Added to cart</p>',
      '<button type="button" class="atc-toast__close" aria-label="Close">',
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4 20 20M20 4 4 20"/></svg>',
      '</button>',
      '<div class="atc-toast__product">',
      '<span class="atc-toast__media">',
      '<img alt="" width="104" height="104">',
      '<span class="atc-toast__qty" hidden></span>',
      '</span>',
      '<span class="atc-toast__info">',
      '<span class="atc-toast__title"></span>',
      '<span class="atc-toast__meta"></span>',
      '</span>',
      '</div>',
      '<div class="atc-toast__actions">',
      `<a class="atc-toast__btn atc-toast__btn--cart" href="${cartUrl()}">View cart</a>`,
      '<a class="atc-toast__btn atc-toast__btn--checkout" href="/checkout">Checkout</a>',
      '</div>',
      '<span class="atc-toast__timer"></span>'
    ].join('');

    els = {
      media: toast.querySelector('.atc-toast__media'),
      image: toast.querySelector('.atc-toast__media img'),
      qty: toast.querySelector('.atc-toast__qty'),
      title: toast.querySelector('.atc-toast__title'),
      meta: toast.querySelector('.atc-toast__meta'),
      timer: toast.querySelector('.atc-toast__timer')
    };

    toast.style.setProperty('--atc-toast-duration', `${DURATION}ms`);
    toast.querySelector('.atc-toast__close').addEventListener('click', hide);

    // The timer bar drives the dismiss, so hovering pauses both the bar and the
    // hide together (CSS pauses the animation) with no second timer to sync.
    els.timer.addEventListener('animationend', hide);

    document.addEventListener('keydown', (evt) => {
      if (evt.key === 'Escape') hide();
    });

    document.body.appendChild(toast);
  }

  function hide() {
    clearTimeout(fallbackTimer);
    if (toast) toast.classList.remove('is-visible');
  }

  /**
   * Swaps the thumbnail without ever showing the previous product's photo next
   * to the new product's name.
   *
   * Assigning .src directly keeps painting the old image until the new one has
   * downloaded, and back-to-back adds can settle out of order (a slow first
   * image landing after a fast second one, leaving the wrong photo up). So:
   * decode off-screen first, then swap, and drop any result that a later add
   * has already superseded.
   *
   * @param {string} [url] - Shopify image URL, if the product has one.
   */
  function setImage(url) {
    const token = ++imageToken;

    if (!url) {
      els.media.hidden = true;
      return;
    }
    els.media.hidden = false;

    // 2x the 104px tile, so it stays sharp on retina screens.
    const src = `${url.split('?')[0]}?width=240`;
    if (els.image.getAttribute('src') === src) return;

    const apply = () => {
      if (token !== imageToken) return;
      els.image.src = src;
      els.image.classList.remove('is-loading');
    };

    const preload = new Image();
    preload.src = src;

    // Cached images (the same charm added twice) resolve synchronously, so this
    // stays instant in the common case and only dims on a genuine download.
    if (preload.complete) {
      apply();
      return;
    }
    els.image.classList.add('is-loading');
    if (preload.decode) {
      preload.decode().then(apply).catch(apply);
    } else {
      preload.onload = apply;
      preload.onerror = apply;
    }
  }

  /**
   * @param {object} item - Line item from the cart response.
   */
  function show(item) {
    build();

    const title = item.product_title || item.title || 'Item';
    els.title.textContent = title;

    // "Default Title" is Shopify's placeholder for single-variant products.
    const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    els.meta.textContent = variant;
    els.meta.hidden = !variant;

    // Quantity shows as a badge on the thumbnail, and only when it's worth
    // saying (a single unit is the normal case).
    const qty = item.quantity || 1;
    els.qty.textContent = qty;
    els.qty.hidden = qty < 2;

    setImage(item.featured_image ? item.featured_image.url : item.image);

    // Restart the countdown from the top if a second add lands mid-toast.
    toast.classList.remove('is-visible');
    void toast.offsetWidth; // eslint-disable-line no-void
    toast.classList.add('is-visible');

    clearTimeout(fallbackTimer);
    // No timer-bar animation to end when motion is reduced, so hide on a timer.
    if (prefersReducedMotion()) fallbackTimer = setTimeout(hide, DURATION);
  }

  function handleResponse(data) {
    const item = parseItem(data);
    if (item) show(item);
  }

  const nativeFetch = window.fetch;
  window.fetch = function (...args) {
    const promise = nativeFetch.apply(this, args);
    const input = args[0];
    const url = typeof input === 'string' ? input : input && input.url;

    if (!isCartAdd(url)) return promise;

    return promise.then((response) => {
      // clone() so the theme still gets an unread body to parse.
      if (response.ok) {
        response
          .clone()
          .json()
          .then(handleResponse)
          .catch(() => {});
      }
      return response;
    });
  };

  // Some apps still add via XMLHttpRequest.
  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (isCartAdd(url)) {
      this.addEventListener('load', () => {
        if (this.status < 200 || this.status >= 300) return;
        try {
          handleResponse(JSON.parse(this.responseText));
        } catch (e) {
          // Not JSON; nothing to show.
        }
      });
    }
    return nativeOpen.call(this, method, url, ...rest);
  };
})();
